import { useEffect, useRef } from "react";
import {
  IBMPlexSans_400Regular,
  IBMPlexSans_500Medium,
  IBMPlexSans_600SemiBold,
  IBMPlexSans_700Bold,
  useFonts as useSansFonts,
} from "@expo-google-fonts/ibm-plex-sans";
import {
  IBMPlexMono_400Regular,
  IBMPlexMono_700Bold,
  useFonts as useMonoFonts,
} from "@expo-google-fonts/ibm-plex-mono";
import { useQueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { Stack } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import * as Sentry from "@sentry/react-native";
import Toast from "react-native-toast-message";

import { AppLifecycle, useStartupUpdateGate } from "~/lib/app-updates";
import { BackendStatusOverlay } from "~/components/BackendStatusOverlay";
import { MfaGate } from "~/components/composed/MfaGate";
import { QuerySessionBoundary } from "~/components/QuerySessionBoundary";
import { AuthProvider, useAuth } from "~/hooks/useAuth";
import { queryClient } from "~/lib/query-client";
import { persistOptions } from "~/lib/query-persist";
import { migrateGuestToRemote } from "~/state/sync";
import { ThemeProvider, useTheme } from "~/styles/theme";

void SplashScreen.preventAutoHideAsync();

function ThemedStack() {
  const { scheme, colors } = useTheme();
  return (
    <>
      <Stack
        screenOptions={{
          headerShown: false,
          headerStyle: { backgroundColor: colors.primary },
          contentStyle: {
            backgroundColor: colors.background,
          },
        }}
      />
      <StatusBar style={scheme === "dark" ? "light" : "dark"} />
      <Toast />
    </>
  );
}

function RootLayout() {
  const [sansLoaded, sansError] = useSansFonts({
    IBMPlexSans_400Regular,
    IBMPlexSans_500Medium,
    IBMPlexSans_600SemiBold,
    IBMPlexSans_700Bold,
  });
  const [monoLoaded, monoError] = useMonoFonts({
    IBMPlexMono_400Regular,
    IBMPlexMono_700Bold,
  });
  const fontsReady =
    (sansLoaded && monoLoaded) || Boolean(sansError || monoError);

  // Hold the splash until fonts load and the launch-time update check finishes (4s cap).
  const updateCheckDone = useStartupUpdateGate();
  const ready = fontsReady && updateCheckDone;

  useEffect(() => {
    if (ready) void SplashScreen.hideAsync();
  }, [ready]);

  if (!ready) return null;

  return (
    <ThemeProvider>
      <AppLifecycle />
      <BackendStatusOverlay />
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
      >
        <AuthProvider>
          <MfaGate>
            <QuerySessionBoundary>
              <GuestDataMigration />
              <ThemedStack />
            </QuerySessionBoundary>
          </MfaGate>
        </AuthProvider>
      </PersistQueryClientProvider>
    </ThemeProvider>
  );
}

/** When a guest signs up or signs in, copy their on-device gear into the account once. */
function GuestDataMigration() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const migratedFor = useRef<string | null>(null);

  useEffect(() => {
    const userId = user?.id ?? null;
    if (!userId || migratedFor.current === userId) return;
    migratedFor.current = userId;
    void migrateGuestToRemote(queryClient, userId).catch(() => {
      migratedFor.current = null;
    });
  }, [user?.id, queryClient]);

  return null;
}

export default Sentry.wrap(RootLayout);
